import { useState, useMemo, useCallback, useRef } from 'react';
import { groupData, getTableHeader, handleCellClick, vidMap, vidOrder } from './rep-fin-data-grouping';
import './rep-fin-report-total.css';
import RepFinReportGraph from './rep-fin-report-graph';
import RepFinReportDetail from './rep-fin-report-detail';
import { Table, Spin } from 'antd';
import { isMobile } from '../../../extensions/design';
import { useEffect } from 'react';
import { useExcelData } from "../rep-excel-data-provider";

const chartColors = [
  '#fbc02d', '#1976d2', '#0288d1', '#388e3c', '#d32f2f', '#7b1fa2', '#ffa000', '#0288d1', '#388e3c', '#d32f2f', '#7b1fa2', '#ffa000'
];

// Общий финансовый отчет в виде таблицы (с вызовом детализации)
function RepFinReportTotal({ filters, data, onRefresh, refreshTrigger, loading }) {
  const [chartType, setChartType] = useState('pie');
  const [detailData, setDetailData] = useState(null);
  const [expandedDetailGroups, setExpandedDetailGroups] = useState(new Set());
  const [selectedCell, setSelectedCell] = useState(null);
  const { setExcelDataTotal } = useExcelData();

  const { tableRows, tableTotal, rowLabels } = useMemo(() => {
    return groupData(data, filters.reportType);
  }, [data]);

  const handleCellClickCallback = useCallback((rowName, vid, type) => {
    const adjustedType = (rowName === 'Всего' && vid === 'all') ? 'total' : type;

    setSelectedCell({ rowName, vid, type: adjustedType });

    const detailDataResult = handleCellClick(
      data, rowName === 'Всего' ? 'all' : rowName, vid, adjustedType, filters.reportType, rowLabels, tableRows
    );

    if (detailDataResult) {
      setDetailData(detailDataResult);
    }
  }, [data, filters.reportType, rowLabels, tableRows]);


  const closeDetail = useCallback(() => {
    setDetailData(null);
    setExpandedDetailGroups(new Set());
    setSelectedCell(null);
  }, []);

  useEffect(() => {
    closeDetail();
  }, [refreshTrigger]);

  const isCellSelected = useCallback((rowName, vid, type) => {
    return selectedCell && 
           selectedCell.rowName === rowName && 
           selectedCell.vid === vid && 
           selectedCell.type === type;
  }, [selectedCell]);

  const dataSource = tableRows.map((row, idx) => {
    const rowObj = {
      key: row.name,
      name: row.name,
      total: row.total,
    };
    vidOrder.forEach(v => {
      if (isMobile) {
        rowObj[v] = (row[v].symaPut || 0) - (row[v].symaExp || 0);
      } else {
        rowObj[v + '_put'] = row[v].symaPut;
        rowObj[v + '_exp'] = row[v].symaExp;
      }
    });    
    return rowObj;
  });

  const summaryObj = {
    key: 'summary-row',
    name: 'Всего',
    total: tableRows.reduce((sum, row) => sum + (row.total || 0), 0),
  };
  vidOrder.forEach(v => {
    if (isMobile) {
      summaryObj[v] = (tableTotal[v]?.symaPut || 0) - (tableTotal[v]?.symaExp || 0);
    } else {
      summaryObj[v + '_put'] = tableTotal[v]?.symaPut || 0;
      summaryObj[v + '_exp'] = tableTotal[v]?.symaExp || 0;
    }
  });
  const fullDataSource = [...dataSource, summaryObj];

  const columns = isMobile
    ? [
        {
          title: getTableHeader(filters.reportType),
          dataIndex: 'name',
          key: 'name',
          fixed: 'left',
          width: 100,
        },
        ...vidOrder.map(v => ({
          title: vidMap[v],
          dataIndex: v,
          key: v,
          align: 'right',
          width: 70,
          render: (value) => value ? value.toLocaleString() : '',
          onCell: (row) => ({
            className: isCellSelected(row.name, v, 'total') ? 'selected-cell' : 'clickable-cell',
            onClick: () => handleCellClickCallback(row.name, v, 'total'),
          }),
        })),
        {
          title: 'Всего',
          dataIndex: 'total',
          key: 'total',
          align: 'right',
          width: 120,
          render: (value, row, idx) => (
            <span>
              {value ? value.toLocaleString() : ''}
            </span>
          ),
          onCell: (row) => ({
            className: isCellSelected(row.name, 'all', 'total') ? 'selected-cell' : 'clickable-cell',
            onClick: () => handleCellClickCallback(row.name, 'all', 'total'),
          }),
        },
      ]
    : [
        {
          title: getTableHeader(filters.reportType),
          dataIndex: 'name',
          key: 'name',
          fixed: 'left',
          width: 100,
          render: (text) => <span>{text}</span>,
          onCell: () => ({ className: 'cell-center clickable-cell' }),
        },
        ...vidOrder.flatMap(v => ([
          {
            title: <span>Приход</span>,
            dataIndex: v + '_put',
            key: v + '_put',
            align: 'right',
            width: 70,
            render: (value) => value && value !== 0 ? value.toLocaleString() : '',
            onCell: (row) => ({
              className: isCellSelected(row.name, v, 'put') ? 'selected-cell' : 'clickable-cell',
              onClick: () => handleCellClickCallback(row.name, v, 'put'),
            }),
          },
          {
            title: <span>Возврат</span>,
            dataIndex: v + '_exp',
            key: v + '_exp',
            align: 'right',
            width: 70,
            render: (value) => value && value !== 0 ? value.toLocaleString() : '',
            onCell: (row) => ({
              className: isCellSelected(row.name, v, 'exp') ? 'selected-cell' : 'clickable-cell',
              onClick: () => handleCellClickCallback(row.name, v, 'exp'),
            }),
          }
        ])),
        {
          title: 'Всего',
          dataIndex: 'total',
          key: 'total',
          align: 'right',
          width: 120,
          render: (value, row, idx) => (
            <span>
              <span
                className="report-total-colorbox"
                style={{ background: chartColors[idx % chartColors.length] }}
              ></span>
              {value ? value.toLocaleString() : ''}
            </span>
          ),
          onCell: (row) => ({
            className: isCellSelected(row.name, 'all', 'total') ? 'selected-cell' : 'clickable-cell',
            onClick: () => handleCellClickCallback(row.name, 'all', 'total'),
          }),
        },
      ];

  const prevExcelState = useRef(null);
  useEffect(() => {
    if (!columns?.length || !fullDataSource?.length) return;

    const newState = {
      title: 'Общий отчет',
      columnsCount: columns.length,
      rowsCount: fullDataSource.length,
    };

    // Проверяем, изменились ли данные реально
    if (
      prevExcelState.current &&
      prevExcelState.current.columnsCount === newState.columnsCount &&
      prevExcelState.current.rowsCount === newState.rowsCount
    ) {
      return; // ничего не делаем — данные те же
    }

    prevExcelState.current = newState;

    setExcelDataTotal({
      title: 'Общий отчет',
      columns,
      data: fullDataSource,
    });

  }, [columns, fullDataSource]);

  // Custom header for two-row table head
  const customHeader = () => {
    if (isMobile) return (
      <thead>
      <tr>
      <th
        rowSpan={1}
        className="header-cell header-cell-main"
        onClick={closeDetail}
      >
        {getTableHeader(filters.reportType)}
      </th>
      {vidOrder.map(v => (
        <th key={v} colSpan={1} className="header-cell">{vidMap[v]}</th>
      ))}
      <th rowSpan={1} className="header-cell">Всего</th>
    </tr>
    </thead>
    );
    return (
      <thead>
        <tr>
          <th
            rowSpan={2}
            className="header-cell header-cell-main"
            onClick={closeDetail}
          >
            {getTableHeader(filters.reportType)}
          </th>
          {vidOrder.map(v => (
            <th key={v} colSpan={2} className="header-cell">{vidMap[v]}</th>
          ))}
          <th rowSpan={2} className="header-cell">Всего</th>
        </tr>
        <tr>
          {vidOrder.map(v => [
            <th key={v + '-put'} className="header-cell">Приход</th>,
            <th key={v + '-exp'} className="header-cell">Возврат</th>
          ])}
        </tr>
      </thead>
    );
  };

  if (isMobile)
    return (
      <div className="abon-total report-total-flex">
        {loading ? (
        <div className="report-loading">
          <Spin size="large" />
        </div>
      ) : (
        <>
        <div className="date-period">
          Период {filters.dateFrom?.format('DD.MM.YY')} - {filters.dateTo?.format('DD.MM.YY')}
        </div>
      <div className="report-total-main">
        <div>
          <Table
            columns={columns}
            dataSource={fullDataSource}
            pagination={false}
            bordered
            size="small"
            rowClassName={(row) => row && row.key === 'summary-row' ? 'total-row' : ''}
            components={{ header: { wrapper: customHeader } }}
            className="report-total-table-normal"
          />
        </div>
        {detailData && (
          <div className="report-total-detail-wrapper">
            <RepFinReportDetail 
              detailData={detailData.items} 
              onClose={closeDetail}
              detailType={detailData.type}
              vidName={detailData.vidName}
              expandedGroups={expandedDetailGroups}
              setExpandedGroups={setExpandedDetailGroups}
              selectedRowName={detailData.kassaName || detailData.periodName || 'Всего'}
            />
          </div>
        )}
        {!detailData && (<RepFinReportGraph
          data={tableRows.map((row) => ({ name: row.name, total: row.total }))}
          chartType={chartType}
          setChartType={setChartType}
          reportType={filters.reportType}
        />
      )}
      </div>
    </>
    )}
    </div>
  );

  
  return (
    <div className="report-total-flex">
      {loading ? (
      <div className="report-loading">
        <Spin size="large" />
      </div>
    ) : (
      <>
      <RepFinReportGraph
        data={tableRows.map((row) => ({ name: row.name, total: row.total }))}
        chartType={chartType}
        setChartType={setChartType}
        reportType={filters.reportType}
      />
      <div className="report-total-main">
        <div className="print-title">
          Финансовый отчет за период {filters.startDate?.format("DD.MM.YYYY")} — {filters.endDate?.format("DD.MM.YYYY")}
        </div>
        <div>
          <Table
            columns={columns}
            dataSource={fullDataSource}
            pagination={false}
            bordered
            size="small"
            rowClassName={(row) => row && row.key === 'summary-row' ? 'total-row' : ''}
            components={{ header: { wrapper: customHeader } }}
            className="report-total-table-normal"
          />
        </div>
        {detailData && (
          <div className="report-total-detail-wrapper">
            <RepFinReportDetail 
              detailData={detailData.items} 
              onClose={closeDetail}
              detailType={detailData.type}
              vidName={detailData.vidName}
              expandedGroups={expandedDetailGroups}
              setExpandedGroups={setExpandedDetailGroups}
              selectedRowName={detailData.kassaName || detailData.periodName || 'Всего'}
            />
          </div>
        )}
      </div>
      </>
    )}
    </div>
  );
}

export default RepFinReportTotal;